// src/pages/MonitoringDetail.tsx
import { useState, useEffect } from 'react';
import { useParams, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CRAWL_ALREADY_RUNNING_CODE, getProject, triggerCrawl, getCrawlStatus, getSnapshotsPaginated, cancelCrawlJob, pauseProject, resumeProject, type MonitoringApiError, type MonitoringProject, type CrawlStatus } from '../api/monitoring';
import { useToast } from '../hooks/useToast';
import { ConfirmModal } from '../components/Modal/Modal';
import styles from './MonitoringDetail.module.css';
import { FiRefreshCw, FiArrowLeft, FiGlobe, FiClock, FiLink, FiX, FiPause, FiPlay } from 'react-icons/fi';

import { logger } from '../utils/logger';
import { userMessage } from '../utils/userMessage';

export default function MonitoringDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [crawlCompleted, setCrawlCompleted] = useState(false);
  const [prevStatus, setPrevStatus] = useState<string | undefined>(undefined);
  // Track if we should be polling - only for actively running crawls
  const [activeCrawlPolling, setActiveCrawlPolling] = useState(false);
  // Track crawl progress
  const [crawlStatus, setCrawlStatus] = useState<CrawlStatus | null>(null);
  const [isPollingProgress, setIsPollingProgress] = useState(false);
  const [pollErrorCount, setPollErrorCount] = useState(0);
  // P1.2: in-app confirm dialogs instead of native confirm().
  const [resumeConfirmOpen, setResumeConfirmOpen] = useState(false);
  const [pauseConfirmOpen, setPauseConfirmOpen] = useState(false);
  const [cancelJobId, setCancelJobId] = useState<string | null>(null);

  // Query for project data
  const {
    data: project,
    isLoading,
    isError,
  } = useQuery<MonitoringProject>({
    queryKey: ['project', id],
    queryFn: () => getProject(id!),
    // Only poll when a crawl is actively running - increased from 10 seconds to 30 seconds
    refetchInterval: activeCrawlPolling ? 30000 : false,
    // Reduce window focus refetching
    refetchOnWindowFocus: false,
    // Cache for 5 minutes for non-polling requests
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Query for total URLs count (fetch first page to get total count)
  const { data: snapshotsData } = useQuery({
    queryKey: ['snapshots-count', id],
    queryFn: () => getSnapshotsPaginated(id!, 1, 1), // Fetch only 1 item to get total count
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
  
  // Handle project status transitions after the initial load.
  useEffect(() => {
    if (!project?.status) return;

    if (prevStatus === undefined) {
      setPrevStatus(project.status);
      return;
    }

    if (prevStatus !== project.status) {
      // Project status changed, refresh snapshot & changes data
      queryClient.invalidateQueries({ queryKey: ['snapshots', id] });
      queryClient.invalidateQueries({ queryKey: ['snapshots-count', id] });
      queryClient.invalidateQueries({ queryKey: ['changes', id] });
    }

    setPrevStatus(project.status);
  }, [project?.status, prevStatus, id, queryClient]);

  const crawlMutation = useMutation({
    mutationFn: (force?: boolean) => triggerCrawl(id!, undefined, force),
    onSuccess: (updatedProject) => {
      // Optimistically stamp lastCheck to now so the header reflects crawl initiation
      // immediately. The invalidateQueries call after completion will overwrite this
      // with the accurate backend timestamp once the crawl finishes.
      const now = new Date().toISOString();
      queryClient.setQueryData(['project', id], {
        ...updatedProject,
        lastCheck: now,
      });

      // Immediately invalidate snapshots and changes to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['snapshots', id] });
      queryClient.invalidateQueries({ queryKey: ['changes', id] });

      // Start active polling when a crawl is manually triggered
      setActiveCrawlPolling(true);

      // Start polling for crawl progress
      setIsPollingProgress(true);
      setCrawlStatus(null); // Reset previous status
      setPollErrorCount(0); // Reset error counter

      // The crawl has started; completion is shown only after the progress
      // endpoint reports a terminal completed state.
      setCrawlCompleted(false);
    },
    onError: (error: unknown) => {
      const apiError = error as MonitoringApiError;
      logger.error('Failed to trigger crawl:', apiError);

      if (apiError.code === CRAWL_ALREADY_RUNNING_CODE) {
        const details = apiError.details as { message?: string } | undefined;
        toast.warning(
          'Crawl Already Running',
          details?.message || apiError.message || 'A crawl is already running. Force restart only if it appears stuck.',
          {
            duration: 0,
            action: {
              label: 'Force Crawl',
              onClick: () => crawlMutation.mutate(true),
            },
          },
        );
        return;
      }

      // Handle insufficient credits error
      if (apiError.code === 'INSUFFICIENT_CREDITS') {
        const details = apiError.details as { message?: string; required_credits?: number; upgrade_url?: string } | undefined;
        toast.error(
          'Insufficient Credits',
          details?.message || `Estimated ${details?.required_credits || 0} credits needed to crawl this project.`,
          {
            duration: 0, // Don't auto-dismiss
            action: details?.upgrade_url ? {
              label: 'Upgrade Plan',
              onClick: () => {
                navigate('/admin/billing/upgrade');
              }
            } : undefined
          }
        );
      } else {
        // Generic error notification
        toast.error(
          'Crawl Failed',
          apiError.message || 'Failed to start crawl. Please try again.',
          {
            duration: 7000
          }
        );
      }
    },
  });

  // 🛑 Cancel crawl job mutation
  const cancelCrawlMutation = useMutation({
    mutationFn: (jobId: string) => cancelCrawlJob(jobId),
    onSuccess: () => {
      // Stop polling
      setIsPollingProgress(false);
      setActiveCrawlPolling(false);
      setCancelJobId(null);

      // Polling is stopped, so the poll loop will never deliver the server's
      // 'cancelled' state — without this the last-polled snapshot (status
      // 'running', e.g. "Crawling: 0/106") stays frozen on screen forever and
      // keeps the Crawl Now button locked. Mark it cancelled locally, show
      // the confirmation banner briefly, then clear it.
      setCrawlStatus(prev => (prev ? { ...prev, status: 'cancelled' } : prev));
      setTimeout(() => setCrawlStatus(null), 5000);

      // Refresh project data
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      queryClient.invalidateQueries({ queryKey: ['snapshots', id] });
      queryClient.invalidateQueries({ queryKey: ['changes', id] });
    },
    onError: async () => {
      setCancelJobId(null);

      // The most common failure is a 400 because the job already reached a
      // terminal state server-side (e.g. cancelling from a stale banner that
      // froze on "running"). Previously this had no handler at all, so the
      // click silently did nothing and the frozen banner persisted. Re-fetch
      // the real status once: if the job is no longer active, sync the UI to
      // that terminal state and clear; only surface an error if a crawl is
      // genuinely still running and the cancel truly failed.
      try {
        const status = await getCrawlStatus(id!);
        setCrawlStatus(status);
        if (status.status !== 'running' && status.status !== 'pending') {
          setIsPollingProgress(false);
          setActiveCrawlPolling(false);
          setTimeout(() => setCrawlStatus(null), 5000);
          queryClient.invalidateQueries({ queryKey: ['project', id] });
          queryClient.invalidateQueries({ queryKey: ['snapshots', id] });
          queryClient.invalidateQueries({ queryKey: ['changes', id] });
        } else {
          toast.error('Cancel Failed', 'Could not cancel the crawl job. Please try again.');
        }
      } catch {
        toast.error('Cancel Failed', 'Could not cancel the crawl job. Please refresh the page.');
      }
    }
  });

  // ⏸️ Pause project mutation
  const pauseMutation = useMutation({
    mutationFn: () => pauseProject(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      queryClient.invalidateQueries({ queryKey: ['monitoring-projects'] });
    }
  });

  // ▶️ Resume project mutation
  const resumeMutation = useMutation({
    mutationFn: () => resumeProject(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      queryClient.invalidateQueries({ queryKey: ['monitoring-projects'] });
    }
  });

  // Auto-check for active crawl on page load (e.g., after project creation)
  useEffect(() => {
    if (!id || isPollingProgress || crawlStatus) return;

    // Check crawl status on initial page load to detect running crawls
    const checkInitialCrawlStatus = async () => {
      try {
        logger.log('🔍 Checking for active crawl on page load...');
        const status = await getCrawlStatus(id);
        if (status.status === 'running' || status.status === 'pending') {
          logger.log('🚀 Detected active crawl on page load, starting progress polling');
          setCrawlStatus(status);
          setIsPollingProgress(true);
          setActiveCrawlPolling(true);
          setPollErrorCount(0);
        }
      } catch {
        // No active crawl or error fetching - that's fine
        logger.log('ℹ️ No active crawl detected on page load');
      }
    };

    checkInitialCrawlStatus();
  }, [id]); // Only run once when id is available

  // Manage polling based on crawl trigger, not project status
  useEffect(() => {
    let stopPollingTimer: ReturnType<typeof setTimeout> | undefined;

    // Only enable polling if we manually triggered a crawl
    // Don't poll just because project status is 'active' - that's the default state
    if (crawlMutation.isPending) {
      setActiveCrawlPolling(true);
    } else if (activeCrawlPolling && !crawlMutation.isPending && !isPollingProgress) {
      // Keep polling for a bit after crawl completes to catch final state
      stopPollingTimer = setTimeout(() => {
        setActiveCrawlPolling(false);
      }, 60000); // Stop polling after 1 minute
    }

    return () => {
      if (stopPollingTimer) {
        clearTimeout(stopPollingTimer);
      }
    };
  }, [crawlMutation.isPending, activeCrawlPolling, isPollingProgress]);

  // Poll for crawl progress every 2 seconds while crawling
  useEffect(() => {
    if (!isPollingProgress || !id) return;

    const pollProgress = async () => {
      try {
        const status = await getCrawlStatus(id);
        setCrawlStatus(status);
        setPollErrorCount(0); // Reset error count on success

        // Stop polling when crawl is complete, failed, or cancelled
        if (status.status === 'completed' || status.status === 'failed' || status.status === 'cancelled') {
          logger.log(`🛑 Stopping progress polling - Status: ${status.status}`);
          setIsPollingProgress(false);

          // Refresh project data and tables (including the dashboard list so
          // last-crawl timestamps stay in sync across views)
          queryClient.invalidateQueries({ queryKey: ['project', id] });
          queryClient.invalidateQueries({ queryKey: ['monitoring-projects'] });
          queryClient.invalidateQueries({ queryKey: ['snapshots', id] });
          queryClient.invalidateQueries({ queryKey: ['changes', id] });

          // Show completion or error message
          if (status.status === 'completed') {
            setCrawlCompleted(true);
            setTimeout(() => {
              setCrawlCompleted(false);
              setCrawlStatus(null);
            }, 5000);
          } else if (status.status === 'failed') {
            // Keep error visible for 15 seconds
            setTimeout(() => {
              setCrawlStatus(null);
            }, 15000);
          } else if (status.status === 'cancelled') {
            // Show the cancelled banner briefly, then clear — previously this
            // case was missing, so a crawl cancelled from another tab/session
            // left the banner on screen indefinitely.
            setTimeout(() => {
              setCrawlStatus(null);
            }, 5000);
          }
        }
      } catch (error) {
        logger.warn('⚠️ Failed to fetch crawl progress (will retry):', error);

        // Increment error count
        setPollErrorCount(prev => prev + 1);

        // Only stop polling after 5 consecutive failures
        // This handles temporary 502/503 errors during heavy backend processing
        if (pollErrorCount >= 5) {
          logger.error('❌ Too many consecutive polling failures, stopping polling');
          setIsPollingProgress(false);

          // Refresh tables anyway - crawl might have completed
          queryClient.invalidateQueries({ queryKey: ['project', id] });
          queryClient.invalidateQueries({ queryKey: ['snapshots', id] });
          queryClient.invalidateQueries({ queryKey: ['changes', id] });
        } else {
          logger.log(`🔄 Continuing to poll (error ${pollErrorCount + 1}/5)`);
        }
      }
    };

    // Poll immediately
    pollProgress();

    // Then poll every 5 seconds (reduced from 2s to reduce server load)
    const interval = setInterval(pollProgress, 5000);

    return () => clearInterval(interval);
  }, [isPollingProgress, id, queryClient, pollErrorCount]);

  const handleBackClick = () => {
    navigate('/monitoring');
  };

  if (isLoading) {
    return (
      <div className={styles.loadingWrapper}>
        <div className={styles.loadingSpinner}>
          <FiRefreshCw className={styles.spinIcon} size={24} />
        </div>
        <span>Loading project details...</span>
      </div>
    );
  }

  if (isError || !project) {
    return (
      <div className={styles.errorWrapper}>
        <div className={styles.errorContent}>
          <h3>Project Not Found</h3>
          <p>The monitoring project you're looking for doesn't exist or you don't have access to it.</p>
          <button onClick={handleBackClick} className={styles.backToListButton}>
            <FiArrowLeft size={16} />
            Back to Monitoring
          </button>
        </div>
      </div>
    );
  }

  const isActiveCrawlStatus = crawlStatus?.status === 'running' || crawlStatus?.status === 'pending';
  const isCrawlInProgress = crawlMutation.isPending || isPollingProgress || isActiveCrawlStatus;
  const activeCrawlJobId = isActiveCrawlStatus ? crawlStatus?.job_id : undefined;
  const isCrawlDisabled = isCrawlInProgress || project.status === 'paused';
  const showUpdatedState = crawlCompleted && !isCrawlInProgress;
  const crawlButtonTitle = project.status === 'paused'
    ? 'Project is paused'
    : isCrawlInProgress
      ? 'A crawl is already running for this project'
      : 'Start a new crawl now';
  const crawlButtonLabel = isCrawlInProgress ? 'Crawling...' : showUpdatedState ? 'Updated!' : 'Crawl Now';
  const lastCrawlTime = project.lastCheck ? new Date(project.lastCheck).toLocaleString() : 'Never';
  const lastCrawlLabel = project.lastCheck
    ? new Date(project.lastCheck).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Never';

  return (
    <div className={styles.pageWrapper}>
      {/* Header Section */}
      <div className={styles.headerSection}>
        <div className={styles.headerTop}>
          <button onClick={handleBackClick} className={styles.backButton}>
            <FiArrowLeft size={16} />
            <span>Back to Projects</span>
          </button>
        </div>
        
        <div className={styles.headerMain}>
          <div className={styles.projectInfo}>
            <div className={styles.projectTitle}>
              <h1>{project.name}</h1>
              <span className={styles.projectStatus} data-status={project.status}>{project.status}</span>
            </div>
            
            <div className={styles.projectMeta}>
              <div className={styles.metaItem}>
                <FiLink size={14} />
                <span className={styles.urlCount}>
                  {snapshotsData ? `${snapshotsData.total.toLocaleString()} URLs monitored` : 'Loading...'}
                </span>
              </div>
              <div className={styles.metaItem} title={`Last crawl: ${lastCrawlTime}`}>
                <FiClock size={14} />
                <span>Last crawl: <time dateTime={project.lastCheck} aria-label={lastCrawlTime}>{lastCrawlLabel}</time></span>
              </div>
              {project.url && (
                <details className={styles.sourceDetails}>
                  <summary><FiGlobe size={14} />Sitemap</summary>
                  <a href={project.url} target="_blank" rel="noopener noreferrer">{project.url}</a>
                </details>
              )}
            </div>
          </div>
          
          <div className={styles.headerActions}>
            {project.status === 'paused' ? (
              <button
                className={styles.resumeButton}
                onClick={() => setResumeConfirmOpen(true)}
                disabled={resumeMutation.isPending}
                title="Resume monitoring"
              >
                <FiPlay size={16} />
                {resumeMutation.isPending ? 'Resuming...' : 'Resume'}
              </button>
            ) : isActiveCrawlStatus ? (
              <button
                className={styles.cancelCrawlButton}
                onClick={() => activeCrawlJobId && setCancelJobId(activeCrawlJobId)}
                disabled={!activeCrawlJobId || cancelCrawlMutation.isPending}
                title={activeCrawlJobId ? 'Cancel the current crawl job' : 'Waiting for crawl job details'}
              >
                <FiX size={16} />
                {cancelCrawlMutation.isPending ? 'Cancelling...' : 'Cancel Crawl'}
              </button>
            ) : (
              <button
                className={styles.pauseButton}
                onClick={() => setPauseConfirmOpen(true)}
                disabled={pauseMutation.isPending}
                title="Pause monitoring"
              >
                <FiPause size={16} />
                {pauseMutation.isPending ? 'Pausing...' : 'Pause'}
              </button>
            )}
            <button
              className={`${styles.crawlButton} ${showUpdatedState ? styles.crawlCompleted : ''}`}
              onClick={() => crawlMutation.mutate(undefined)}
              disabled={isCrawlDisabled}
              title={crawlButtonTitle}
              aria-describedby={project.status === 'paused' ? 'paused-crawl-help' : undefined}
            >
              <FiRefreshCw size={16} className={isCrawlInProgress ? styles.spinning : ''} />
              {crawlButtonLabel}
            </button>
          </div>
        </div>
        {project.status === 'paused' && <p id="paused-crawl-help" className={styles.pausedHint}>Resume monitoring to enable crawls.</p>}

        {/* Crawl Progress Bar */}
        {crawlStatus && (
          <div className={`${styles.progressSection} ${crawlStatus.status === 'failed' ? styles.progressError : ''}`}>
            <div className={styles.progressInfo}>
              <div className={styles.progressText}>
                <span className={styles.progressLabel}>
                  {crawlStatus.status === 'running' && `Crawling: ${crawlStatus.urls_crawled} / ${crawlStatus.urls_total} URLs`}
                  {crawlStatus.status === 'completed' && `✅ Completed: ${crawlStatus.urls_crawled} / ${crawlStatus.urls_total} URLs`}
                  {crawlStatus.status === 'failed' && `❌ Failed: ${crawlStatus.urls_crawled} / ${crawlStatus.urls_total} URLs`}
                  {crawlStatus.status === 'pending' && `⏳ Pending: ${crawlStatus.urls_total} URLs`}
                  {crawlStatus.status === 'cancelled' && `🚫 Cancelled: ${crawlStatus.urls_crawled} / ${crawlStatus.urls_total} URLs`}
                </span>
                <div className={styles.progressActions}>
                  <span className={styles.progressPercentage}>
                    {crawlStatus.progress_percentage}%
                  </span>
                  {/* 🛑 Cancel button for running/pending jobs */}
                  {(crawlStatus.status === 'running' || crawlStatus.status === 'pending') && crawlStatus.job_id && (
                    <button
                      className={styles.cancelButton}
                      onClick={() => setCancelJobId(crawlStatus.job_id!)}
                      disabled={cancelCrawlMutation.isPending}
                      title="Cancel current crawl job"
                    >
                      <FiX size={14} />
                      {cancelCrawlMutation.isPending ? 'Cancelling...' : 'Cancel Job'}
                    </button>
                  )}
                </div>
              </div>
              {crawlStatus.current_url && (
                <div className={styles.currentUrl}>
                  {crawlStatus.status === 'running' ? 'Current: ' : 'Last: '}{crawlStatus.current_url}
                </div>
              )}
              {crawlStatus.error_message && (
                <div className={styles.errorMessage} role="alert">
                  {userMessage(crawlStatus.error_message, 'Crawl could not finish. Please try again.')}
                </div>
              )}
            </div>
            <div className={styles.progressBarContainer}>
              <div
                className={`${styles.progressBarFill} ${
                  crawlStatus.status === 'failed' ? styles.progressBarError :
                  crawlStatus.status === 'completed' ? styles.progressBarSuccess : ''
                }`}
                style={{ width: `${crawlStatus.progress_percentage}%` }}
              />
            </div>
            <div className={styles.progressStats}>
              <span>✅ {crawlStatus.snapshots_created} snapshots</span>
              <span>🔍 {crawlStatus.changes_detected} changes</span>
              {crawlStatus.urls_failed > 0 && (
                <span className={styles.failedUrls}>❌ {crawlStatus.urls_failed} failed</span>
              )}
            </div>
          </div>
        )}

        {/* Stats Section */}
        <div className={styles.statsBar} aria-label="Project summary">
          <div className={styles.statItem}>
            <span className={styles.statValue}>{project.changeCount}</span>
            <span className={styles.statLabel}>Total Changes</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statValue}>{project.alertCount}</span>
            <span className={styles.statLabel}>Active Alerts</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statValue}>{project.schedule.frequency}</span>
            <span className={styles.statLabel}>Frequency</span>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <nav className={styles.tabNavigation} aria-label="Project sections">
        <div className={styles.tabsContainer}>
          <NavLink
            to="."
            end
            className={({ isActive }) =>
              `${styles.tab} ${isActive ? styles.activeTab : ''}`
            }
          >
            <span>Snapshots</span>
          </NavLink>
          <NavLink
            to="changes"
            className={({ isActive }) =>
              `${styles.tab} ${isActive ? styles.activeTab : ''}`
            }
          >
            <span>Changes</span>
          </NavLink>
          <NavLink
            to="settings"
            className={({ isActive }) =>
              `${styles.tab} ${isActive ? styles.activeTab : ''}`
            }
          >
            <span>Settings</span>
          </NavLink>
        </div>
      </nav>

      {/* Content Section */}
      <div className={styles.contentSection}>
        <Outlet context={{ refreshTrigger: crawlCompleted }} />
      </div>

      <ConfirmModal
        isOpen={resumeConfirmOpen}
        onClose={() => setResumeConfirmOpen(false)}
        onConfirm={() => resumeMutation.mutate()}
        title="Resume monitoring"
        message="Resume monitoring for this project?"
        confirmText="Resume"
        cancelText="Cancel"
        variant="primary"
      />
      <ConfirmModal
        isOpen={pauseConfirmOpen}
        onClose={() => setPauseConfirmOpen(false)}
        onConfirm={() => pauseMutation.mutate()}
        title="Pause monitoring"
        message="Pause all scheduled crawls for this project?"
        confirmText="Pause"
        cancelText="Cancel"
        variant="primary"
      />
      <ConfirmModal
        isOpen={cancelJobId !== null}
        onClose={() => setCancelJobId(null)}
        onConfirm={() => {
          if (cancelJobId) cancelCrawlMutation.mutate(cancelJobId);
        }}
        title="Cancel crawl job"
        message="Are you sure you want to cancel this crawl job?"
        confirmText="Cancel job"
        cancelText="Keep running"
        variant="danger"
      />
    </div>
  );
}
